import * as React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type { ScheduleBoardOnDateViewModel } from 'ab-viewModels/scheduleBoardWorkOrdersOnDateView.viewModel';

import type * as ScheduleBoardResourceLookupVM from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardResourceLookup.viewModel';
import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { shouldEnableFeatureFromDate } from '@acceligentllc/shared/utils/timeSheet';

import { ScheduleBoardContext } from '@acceligentllc/shared/enums/scheduleBoardContext';
import { ScheduleBoardProperty } from '@acceligentllc/shared/enums/scheduleBoardProperty';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import * as ScheduleBoardUtil from 'af-utils/scheduleBoard.util';

import ScheduleBoardViewEnum from 'ab-enums/scheduleBoardView.enum';

import { RESOURCE_PLACEHOLDER } from 'ab-common/constants/scheduleBoard';

import Checkbox from 'af-components/Controls/Checkbox';

import type { SelectedWorkOrderModel } from 'af-models/scheduleBoard/scheduleBoard.models';

import CardData from './CardData';

interface OwnProps {
	index: number;
	/** `MM-DD-YYYY` */
	dueDate: string;
	workOrderCode: string;
	forceUnlockOrder?: (workOrderId: string) => void;
	maxResourceItems?: number;
	ignorePlaceholders?: boolean;
	isDragAndDropDisabled: boolean;
	hasPermissionsToEditScheduleBoard: boolean;
	isCalculationsView?: boolean;
	/** prop not used if `readOnly = true` */
	selectOrder?: (workOrder: SelectedWorkOrderModel) => void;
	/** prop not used if `readOnly = true` */
	deselectOrder?: (workOrder: SelectedWorkOrderModel) => void;
	/** prop not used if `readOnly = true` */
	addBlankWorkOrder?: (dueDate: string, index: number) => Promise<void>;
	/** prop not used if `isCalculationsView = true` */
	lastOpenedOrderCode?: string;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const Card: React.FC<Props> = (props) => {
	const {
		columnNumber,
		copiedWorkOrderCode,
		dueDate,
		hasPermissionsToEditScheduleBoard = true,
		ignorePlaceholders,
		index,
		isActive,
		isCalculationsView = false,
		isDragAndDropDisabled = false,
		isMultiSelectModeActive,
		isOrderSelected,
		lastOpenedOrderCode,
		maxResourceItems = 0,
		searchableId,
		workOrderCode,
		addBlankWorkOrder,
		deselectOrder,
		selectOrder,
		forceUnlockOrder,
		workOrder,
		notificationStatusMap,
		workOrderResourceLookupsDict,
		employees,
		equipment,
		temporaryEmployees,
		shouldRoundTo1Min,
	} = props;

	const {
		workOrderResourceLookups,
		hasCardArrow,
	} = React.useMemo(() => {
		const _workOrderResourceLookups: ScheduleBoardResourceLookupVM.Single[] = [];
		let anyResourceMatched = false;
		if (!!workOrderResourceLookupsDict && !!workOrder?.workOrderResourceLookups) {
			for (const _resourceId of workOrder.workOrderResourceLookups) {
				if (_resourceId === RESOURCE_PLACEHOLDER) {
					continue;
				}
				const _resourceLookup = workOrderResourceLookupsDict[_resourceId];
				if (!_resourceLookup) {
					continue;
				}
				_workOrderResourceLookups.push(_resourceLookup);
				const _isResourceMatched = (!!_resourceLookup.employeeId && !!employees?.[_resourceLookup.employeeId]?.isMatched)
					|| (!!_resourceLookup.equipmentId && !!equipment?.[_resourceLookup.equipmentId]?.isMatched)
					|| (!!_resourceLookup.temporaryEmployeeId && !!temporaryEmployees?.[_resourceLookup.temporaryEmployeeId]?.isMatched);
				anyResourceMatched = anyResourceMatched || _isResourceMatched;
			}
		}
		return {
			workOrderResourceLookups: _workOrderResourceLookups,
			hasCardArrow: !!workOrder?.isMatched || anyResourceMatched,
		};
	}, [employees, equipment, temporaryEmployees, workOrder?.isMatched, workOrder?.workOrderResourceLookups, workOrderResourceLookupsDict]);

	const notificationStatus = React.useMemo(() => ScheduleBoardUtil.getGlobalParticipantNotificationStatus(
		workOrderResourceLookups,
		notificationStatusMap,
		workOrder?.excludeFromNotify ?? false
	), [notificationStatusMap, workOrder?.excludeFromNotify, workOrderResourceLookups]);

	const forceUnlockWorkOrder = React.useCallback(() => {
		if (!forceUnlockOrder || !workOrder?.id) {
			// This should never happen
			console.error('[FORCE_UNLOCK_WORK_ORDER ERROR]: Missing unlock action or Work Order id on target');
			return;
		}
		forceUnlockOrder(`${workOrder.id}`);
	}, [forceUnlockOrder, workOrder?.id]);

	const addBlankColumn = React.useCallback(async () => {
		if (!hasPermissionsToEditScheduleBoard) {
			return;
		}
		await addBlankWorkOrder?.(dueDate, index);
	}, [addBlankWorkOrder, dueDate, hasPermissionsToEditScheduleBoard, index]);

	const onCheck = React.useCallback(() => {
		if (!hasPermissionsToEditScheduleBoard) {
			return;
		}
		const action = isOrderSelected ? deselectOrder : selectOrder;
		action?.({ id: workOrder!.id, code: workOrderCode, isCancelled: workOrder?.status === WorkOrderStatus.CANCELED });
	}, [hasPermissionsToEditScheduleBoard, isOrderSelected, deselectOrder, selectOrder, workOrder, workOrderCode]);

	const isCardSelected = isMultiSelectModeActive && isOrderSelected;
	const isCardDisabled = !!workOrder?.locked || !!workOrder?.isDisabled;

	let className = 'schedule-board-card';
	className += isCardDisabled ? ' disabled' : '';
	className += workOrder?.isMatched ? ' highlighted' : '';
	className += isActive ? ' highlighted-active' : '';
	className += workOrder?.isFiltered ? ' filtered' : '';
	const classNameWithSelected = `${className} ${isCardSelected ? ' selected' : ''}`;
	const cardWrapperClassName = `schedule-board-card-wrapper columns-${columnNumber} ${workOrder?.isInternal ? 'internal' : ''}`;

	const renderDraggable = React.useCallback((provided, snapshot) => {
		return (
			<>
				<div
					className={classNameWithSelected}
					id={searchableId}
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					onDoubleClick={workOrder?.locked ? forceUnlockWorkOrder : undefined}
				>
					{isMultiSelectModeActive &&
						<Checkbox
							extraClass="schedule-board-card-checkbox"
							handleChange={onCheck}
							isChecked={isOrderSelected}
						/>
					}
					<CardData
						dueDate={dueDate}
						hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
						ignorePlaceholders={ignorePlaceholders}
						isCalculationsView={isCalculationsView}
						isCardDisabled={isCardDisabled}
						isDragAndDropDisabled={isDragAndDropDisabled}
						isDragging={snapshot.isDragging}
						lastOpenedOrderCode={lastOpenedOrderCode}
						maxResourceItems={maxResourceItems}
						notificationStatus={notificationStatus}
						shouldRoundTimeIntervalsTo1Min={shouldRoundTo1Min}
						workOrderCode={workOrderCode}
					/>
				</div>
				{copiedWorkOrderCode && copiedWorkOrderCode === workOrderCode && (
					<div className={`${className} --copy-placeholder ${!snapshot.isDragging ? 'display-none' : ''}`}>
						<CardData
							dueDate={dueDate}
							hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
							ignorePlaceholders={ignorePlaceholders}
							isCalculationsView={isCalculationsView}
							isCardDisabled={isCardDisabled}
							isCopyPlaceholder={true}
							isDragAndDropDisabled={isDragAndDropDisabled}
							isDragging={snapshot.isDragging}
							lastOpenedOrderCode={lastOpenedOrderCode}
							maxResourceItems={maxResourceItems}
							notificationStatus={notificationStatus}
							shouldRoundTimeIntervalsTo1Min={shouldRoundTo1Min}
							workOrderCode={workOrderCode}
						/>
					</div>
				)}
			</>
		);
	}, [
		className,
		classNameWithSelected,
		copiedWorkOrderCode,
		dueDate,
		forceUnlockWorkOrder,
		hasPermissionsToEditScheduleBoard,
		ignorePlaceholders,
		isCalculationsView,
		isCardDisabled,
		isDragAndDropDisabled,
		isMultiSelectModeActive,
		isOrderSelected,
		lastOpenedOrderCode,
		maxResourceItems,
		notificationStatus,
		onCheck,
		searchableId,
		workOrder?.locked,
		workOrderCode,
		shouldRoundTo1Min,
	]);

	return (
		<div className={cardWrapperClassName}>
			<div className="schedule-board-arrow-index-container">
				{
					hasCardArrow ?
						<div className="schedule-board-arrow --visible" /> :
						<div className="schedule-board-card-index pointer" onClick={addBlankColumn}>{index + 1}</div>
				}
			</div>
			<Draggable
				draggableId={ScheduleBoardUtil.generateDroppableId(ScheduleBoardContext.BOARD, ScheduleBoardProperty.WORK_ORDER, dueDate, workOrderCode)}
				index={index}
				isDragDisabled={isCardDisabled || isDragAndDropDisabled || !hasPermissionsToEditScheduleBoard}
			>
				{renderDraggable}
			</Draggable>
		</div>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { dueDate, workOrderCode } = ownProps;
	const { company: { company } } = state;
	const {
		activeSearchItemIndex,
		copiedWorkOrderCode,
		employees,
		equipment,
		temporaryEmployees,
		isMultiSelectModeActive,
		scheduleBoardView,
		searchResultItems,
		selectedWorkOrders,
		weeklyViewSelectMultiple,
		workOrdersByDateDictionary,
	} = state.scheduleBoard;
	const workOrdersOnDateDict: ScheduleBoardOnDateViewModel = workOrdersByDateDictionary[dueDate];
	const workOrder: ScheduleBoardWorkOrderViewModel = workOrdersOnDateDict?.workOrders?.[workOrderCode];

	const columnNumber: number = scheduleBoardView === ScheduleBoardViewEnum.DAILY_VIEW
		? workOrdersOnDateDict?.columnNumbersDict[workOrderCode]
		: ScheduleBoardUtil.getColumnNumberForWorkOrder(workOrder);

	const shouldRoundTo1Min = shouldEnableFeatureFromDate({
		workOrderDueDate: TimeUtils.formatDate(dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
		enableFeatureFrom: company?.timeRoundingPolicyFrom ?? null,
	});

	if (!workOrder) {
		return {
			columnNumber,
			copiedWorkOrderCode,
			isActive: false,
			isMultiSelectModeActive: isMultiSelectModeActive || !!weeklyViewSelectMultiple[dueDate],
			isOrderSelected: false,
			workOrderResourceLookups: [],
			notificationStatusMap: {},
			searchableId: undefined,
			workOrder: null,
			employees,
			equipment,
			temporaryEmployees,
		};
	}

	const { assignedCanceledNotificationStatuses = {}, assignedPublishedNotificationStatuses = {} } = workOrdersOnDateDict ?? {};
	const isWorkOrderCanceled: boolean = workOrder.status === WorkOrderStatus.CANCELED;

	const searchableId = workOrder.id ? ScheduleBoardUtil.generateWorkOrderSearchItemId(workOrder.id.toString()) : undefined;

	const workOrderResourceLookupsDict = isWorkOrderCanceled
		? workOrdersOnDateDict?.canceledWorkOrderResourceLookups
		: workOrdersOnDateDict?.workOrderResourceLookups;

	return {
		columnNumber,
		copiedWorkOrderCode,
		isActive: searchResultItems[activeSearchItemIndex] === searchableId,
		isMultiSelectModeActive: isMultiSelectModeActive || !!weeklyViewSelectMultiple[dueDate],
		isOrderSelected: selectedWorkOrders.some(({ id }) => id === workOrder.id) || !!(weeklyViewSelectMultiple[dueDate]?.[workOrder.id]),
		workOrderResourceLookupsDict,
		notificationStatusMap: isWorkOrderCanceled ? assignedCanceledNotificationStatuses : assignedPublishedNotificationStatuses,
		searchableId,
		workOrder,
		employees,
		equipment,
		temporaryEmployees,
		shouldRoundTo1Min,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(Card);
